/**
 * Tokens couleur des statuts. Un même `StatusTone` (teinte OKLCH partagée
 * via `--status-hue`, ou exception neutral/error) pilote deux surfaces aux
 * couleurs distinctes : `.status-chip` (pilule pastel punchy) et
 * `.status-banner` (grande surface, teinte plus douce).
 *
 * ⚠️ Déclarés sur la classe porteuse, PAS sur `:root` : un token qui
 * référence `var(--status-hue)` résout cette teinte au niveau de son élément
 * de déclaration. Au `:root`, la teinte n'existe pas et tomberait sur le
 * fallback (250) pour tous les statuts.
 *
 * Injectés globalement par `createOdysTheme` via `MuiCssBaseline`.
 */
export const statusSurfaces = {
  ".status-chip": {
    "--status-chip-bg": "oklch(0.9282 0.0375 var(--status-hue, 250))",
    "--status-chip-fg": "oklch(0.3903 0.0894 var(--status-hue, 250))",
  },
  ".status-chip--neutral": {
    "--status-chip-bg": "oklch(0.9282 0 0)",
    "--status-chip-fg": "oklch(0.3903 0 0)",
  },
  ".status-chip--error": {
    "--status-chip-bg": "oklch(0.9282 0 0)",
    "--status-chip-fg": "oklch(0.5226 0.1947 27)",
  },
  ".status-banner": {
    "--status-banner-bg": "oklch(0.96 0.022 var(--status-hue, 250))",
    "--status-banner-fg": "oklch(0.42 0.1 var(--status-hue, 250))",
  },
  ".status-banner--neutral": {
    "--status-banner-bg": "oklch(0.96 0 0)",
    "--status-banner-fg": "oklch(0.45 0 0)",
  },
  ".status-banner--error": {
    "--status-banner-bg": "oklch(0.96 0 0)",
    "--status-banner-fg": "oklch(0.5226 0.1947 27)",
  },
} as const;

/**
 * Vocabulaire des tons de statut, en miroir des surfaces ci-dessus :
 *
 * - un **nombre** = une teinte OKLCH, passée en `--status-hue` ;
 * - `'neutral'` = gris (état terminal inactif) ;
 * - `'error'` = rouge sur gris (état négatif / annulé).
 *
 * `@odys/project-status` déclare le même type de son côté plutôt que de
 * l'importer : ce paquet est consommé par du code serveur, et le faire dépendre
 * du design system y ferait entrer MUI en peer. Les deux définitions étant
 * structurellement identiques, toute divergence casse la compilation de catalog,
 * qui importe les deux.
 */
export type StatusTone = number | "neutral" | "error";

type StatusSurface = "status-chip" | "status-banner";

export type StatusToneStyle = {
  /** Classe(s) de surface à poser sur l'élément porteur. */
  className: string;
  /** Variables CSS à spread dans `sx` (la teinte du ton, ou rien pour les exceptions). */
  vars: Record<string, number>;
};

function toneStyle(surface: StatusSurface, tone: StatusTone): StatusToneStyle {
  if (typeof tone === "number") {
    return { className: surface, vars: { "--status-hue": tone } };
  }
  return { className: `${surface} ${surface}--${tone}`, vars: {} };
}

/**
 * Surface pilule (pastel punchy) : tokens `--status-chip-bg` / `--status-chip-fg`.
 * Préférer le composant `StatusChip`, qui câble déjà les deux.
 */
export function statusChipStyle(tone: StatusTone): StatusToneStyle {
  return toneStyle("status-chip", tone);
}

/**
 * Surface bannière (grande surface, teinte douce) : tokens
 * `--status-banner-bg` / `--status-banner-fg`. Préférer le composant
 * `StatusBanner`.
 */
export function statusBannerStyle(tone: StatusTone): StatusToneStyle {
  return toneStyle("status-banner", tone);
}
